import React, { useEffect, useState } from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'
import { FaInfoCircle } from 'react-icons/fa'
import styled from 'styled-components'

import { campusEnums, personaEnums } from '../../enums'
import { useCampus, usePersona } from '../../hooks'
import useAfterHours from '../../hooks/useAfterHours'
import useGA from '../../hooks/useGa'
import useUserInfo from '../../hooks/useUserInfo'
import { space } from '../../style/design-system'
import SelfEnrollCTA from '../ctas/SelfEnrollCTA'
import ComposedFormHireUMA from '../forms/ComposedFormHireUMA'
import FormCampusSelect from '../forms/FormCampusSelect'
import FormNoCampus from '../forms/FormNoCampus'
import FormWrapperGray from '../forms/FormWrapperGray'
import ModalFullScreen from '../modals/ModalFullScreen'
import { Toggle } from '../modals/Toggle'

const GetStarted = ({ className, displayAsLink, pageLocation }) => {
  const { campus } = useCampus()
  const { persona } = usePersona()
  const isAfterHours = useAfterHours()
  const [isAfterHoursAndIsConf, setIsAfterHoursAndIsConf] = useState(false)
  const { getUserInfo } = useUserInfo()
  const [userInfo, setUserInfo] = useState(null)
  const sendToGA = useGA()

  useEffect(() => {
    if (isAfterHours && window.location.pathname.includes('/confirmation/ready')) {
      setIsAfterHoursAndIsConf(true)
    }
  }, [isAfterHours])

  useEffect(() => {
    async function populateUserInfo() {
      const info = await getUserInfo()
      setUserInfo(info)
    }
    populateUserInfo()
  }, [getUserInfo])

  const renderForm = () => {
    const campusOrPersona =
      campus === campusEnums.codes.ONLINE ||
      campus === campusEnums.codes.CLEARWATER ||
      persona.name === personaEnums.personas.PROGRAM_ONLINE ||
      persona.name === personaEnums.personas.PROGRAM_CLEARWATER

    if (persona.name === personaEnums.personas.EMPLOYER) {
      return <ComposedFormHireUMA className="modal-form" />
    }
    if (campusOrPersona) {
      return (
        <FormWrapperGray>
          <FormNoCampus includeUserErrorModal className="modal-form" />
        </FormWrapperGray>
      )
    }
    return (
      <FormWrapperGray>
        <FormCampusSelect includeUserErrorModal className="modal-form" />
      </FormWrapperGray>
    )
  }

  const renderToggle = () => (
    <Toggle>
      {({ on, toggle }) => (
        <>
          <button
            className={cx(className, 'no-outline pointer px-7 white ml-lg-7 btn-green', {
              'style-link': displayAsLink,
            })}
            onClick={toggle}
            type="button">
            {displayAsLink && (
              <span className="d-none d-lg-inline">
                <FaInfoCircle />
                &nbsp;
              </span>
            )}
            <span>{persona.cta}</span>
          </button>
          {on && <ModalFullScreen closeModal={toggle}>{renderForm()}</ModalFullScreen>}
        </>
      )}
    </Toggle>
  )

  const renderLink = () => (
    <a
      className={`${className} pointer px-7 btn-green ml-lg-7`}
      href={persona.ctaLink}
      target="_blank"
      rel="noopener noreferrer">
      {persona.cta}
    </a>
  )

  if (isAfterHoursAndIsConf) {
    return (
      <SelfEnrollCTA
        beforeSelfEnroll={() => {
          sendToGA({
            action: 'Click',
            category: 'Self Enroll',
            label: `${pageLocation} Button`,
          })
        }}
        buttonText="Self-Enroll"
        className="ml-lg-7"
        userInfo={userInfo}
      />
    )
  }

  return !persona.ctaLink ? renderToggle() : renderLink()
}

GetStarted.propTypes = {
  className: PropTypes.string,
  displayAsLink: PropTypes.bool,
  pageLocation: PropTypes.string.isRequired,
}

GetStarted.defaultProps = {
  className: undefined,
  displayAsLink: false,
}

export default styled(GetStarted)`
  border: none;
  font-size: 1.5rem;
  padding: ${space.c} 0;
  text-align: center;
  &.style-link {
    background: transparent;
    font-size: 1.2rem;
    svg {
      transform: translateY(2px);
    }
  }
`
